// The KNOWLEDGE-TREE model behind the Memory rail (Signal re-layout). The `memory` browse projection answers
// ONE tree level at a time (IFC-MEM-004 navigateTree: this area's child Nodes + its child sub-areas), so the
// design's multi-level tree is assembled from REAL reads: every level in the cache was fetched from the owning
// module, and an area that has never been opened contributes nothing — no guessed children, no guessed count
// (P-SELF). Opening an area re-reads at that area and the new level joins the cache; closing it keeps the
// cache and only hides the rows.
//
// This file holds the model (the level cache + the pure flattening) so the rail component stays a renderer and
// the walk itself is testable without a DOM.

import { useCallback, useState } from 'react';
import { useSurfaceApi } from '../SurfaceApiContext.tsx';
import { useProjection } from '../useProjection.ts';
import type { ProjectionState } from '../useProjection.ts';
import type { MemoryBrowserProjection, TreeLevelView } from '../know-projections.ts';

/** The wiki root area path — the owning module resolves the level (an empty path IS the root). */
export const ROOT_AREA = '';

/** One rendered row of the knowledge tree: an AREA (openable) or a NODE (focusable). */
export interface MemoryTreeRow {
  readonly key: string;
  readonly kind: 'area' | 'node';
  /** The area path for an area row; the Node id for a node row. */
  readonly id: string;
  readonly label: string;
  /** Indent level — 0 at the wiki root. */
  readonly depth: number;
  /** Areas only: whether this area's children are shown. */
  readonly open: boolean;
  /** Areas only: how many child Nodes the LOADED level carries. `undefined` while the area has never been
   *  opened — an unread area's size is not knowable here, so the row shows NO count rather than a zero —
   *  and `undefined` too when that count is ZERO. A level's `children` are its DIRECT articles only
   *  (`_index.md` and every sub-area are excluded by the owning read), so a container area — one that holds
   *  only sub-areas, the ordinary shape of a wiki branch — would print a bare `0` beside rows that visibly
   *  expand beneath it. That zero reads as "this branch is empty", which is not what was counted. The true
   *  subtree total is not knowable without reading every descendant level, so the row says nothing instead. */
  readonly count: number | undefined;
  /** Node rows: this is the Node the reader is on. */
  readonly selected: boolean;
}

/** The tree levels the browser has actually read, keyed by area path. */
export type MemoryLevels = Readonly<Record<string, TreeLevelView>>;

/** The area a Node's repo-relative path sits in (`finance/pricing.md` → `finance`; a root Node → ''). */
export function areaOfPath(path: string): string {
  const slash = path.lastIndexOf('/');
  return slash === -1 ? ROOT_AREA : path.slice(0, slash);
}

/** Every ancestor area of an area path, the path itself included (`a/b/c` → ['a', 'a/b', 'a/b/c']). */
export function ancestorAreas(areaPath: string): readonly string[] {
  if (areaPath === ROOT_AREA) return [];
  const segments = areaPath.split('/');
  return segments.map((_segment, index) => segments.slice(0, index + 1).join('/'));
}

/** An area path's human label — its own last segment, underscores/dashes read as spaces. */
export function areaLabel(areaPath: string): string {
  const segments = areaPath.split('/');
  const own = segments[segments.length - 1] ?? areaPath;
  return own.replace(/[_-]/g, ' ');
}

/** A frontmatter enum (`business_critical`) as sentence text — the value the module recorded, never a
 *  fabricated grade. Only the separator changes. */
export function humaniseEnum(value: string): string {
  const spaced = value.replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** What the walk needs: loaded levels, the human's open areas, and the focused Node. */
export interface MemoryTreeInput {
  readonly levels: MemoryLevels;
  readonly openAreas: ReadonlySet<string>;
  readonly focusNodeId: string | undefined;
}

/** The node rows of one loaded level. Global search is a separate server projection, never a local filter. */
function nodeRows(input: MemoryTreeInput, level: TreeLevelView, depth: number): MemoryTreeRow[] {
  return level.children.map((child) => ({
    key: `n-${child.id}`,
    kind: 'node' as const,
    id: child.id,
    label: child.title,
    depth,
    open: false,
    count: undefined,
    selected: child.id === input.focusNodeId,
  }));
}

/** How deep the rail will walk. Child areas are strictly deeper paths, so this can only bite on a
 *  malformed level — and a blown stack is a far worse failure than a truncated branch. */
const MAX_TREE_DEPTH = 8;

/** An area row's child count — its LOADED level's direct Nodes, and only when there is at least one
 *  (see MemoryTreeRow.count: a container area's honest direct count is a misleading `0`). */
function loadedCount(input: MemoryTreeInput, areaPath: string): number | undefined {
  const own = input.levels[areaPath]?.children.length;
  return own !== undefined && own > 0 ? own : undefined;
}

/** Walk one area: its own Nodes first, then each sub-area (with its subtree when open). */
function walkArea(
  input: MemoryTreeInput,
  areaPath: string,
  depth: number,
  out: MemoryTreeRow[],
): void {
  const level = input.levels[areaPath];
  if (level === undefined || depth > MAX_TREE_DEPTH) return;
  out.push(...nodeRows(input, level, depth));
  for (const childArea of level.childAreas) {
    const open = input.openAreas.has(childArea);
    const nested: MemoryTreeRow[] = [];
    if (open) walkArea(input, childArea, depth + 1, nested);
    const label = areaLabel(childArea);
    out.push({
      key: `a-${childArea}`,
      kind: 'area',
      id: childArea,
      label,
      depth,
      open,
      count: loadedCount(input, childArea),
      selected: false,
    });
    out.push(...nested);
  }
}

/** Flatten the loaded levels into the rail's rows, from the wiki root down. */
export function flattenMemoryTree(input: MemoryTreeInput): readonly MemoryTreeRow[] {
  const rows: MemoryTreeRow[] = [];
  walkArea(input, ROOT_AREA, 0, rows);
  return rows;
}

/** The browse read plus every level it has accumulated. */
export interface MemoryBrowseState {
  readonly projection: ProjectionState<MemoryBrowserProjection>;
  readonly levels: MemoryLevels;
}

/**
 * Read the browse projection at `(area, nodeId)` and KEEP each level it returns. The projection answers one
 * level per read, so the cache is simply every level this session has actually asked for — never a guess at
 * one it has not. A reload replaces the level under its own key and leaves the rest untouched.
 */
export function useMemoryBrowse(area: string, nodeId: string | undefined): MemoryBrowseState {
  const api = useSurfaceApi();
  const [levels, setLevels] = useState<MemoryLevels>({});
  // The level is filed AS THE READ RESOLVES, inside the closure that knows which area was asked for.
  // Reading `area` from render state afterwards would file a level under whichever area the reader had
  // moved to by then — the level of one area recorded as the level of another.
  const load = useCallback(async () => {
    const result = await api.getMemoryBrowser(area, nodeId);
    setLevels((previous) => ({ ...previous, [area]: result.level }));
    return result;
  }, [api, area, nodeId]);
  const projection = useProjection<MemoryBrowserProjection>(load);
  return { projection, levels };
}
